import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry } from '../calendar-entry.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { NotificationEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';

export class CalendarEntryRescheduledEvent extends NotificationEvent {
  static readonly TYPE_ID: string = '8dee51411e1743adad8f4520a47bfcbb';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly seen = false,
      public readonly after: CalendarEntry,
      public readonly before: CalendarEntry,
      public readonly afterId: string,
      public readonly beforeId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return CalendarEntryRescheduledEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CalendarEntryRescheduledEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CalendarEntryRescheduledEvent {
    return new CalendarEntryRescheduledEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'after')),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'before')),
        MapUtils.get<string>(map, 'afterId'),
        MapUtils.get<string>(map, 'beforeId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'afterId': this.afterId,
      'beforeId': this.beforeId,
      'after': context.toData(this.after),
      'before': context.toData(this.before),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new CalendarEntryRescheduledEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.after.clone(),
        this.before.clone(),
        this.afterId,
        this.beforeId,
    ) as this;
  }

  isSame(other: CalendarEntryRescheduledEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.afterId === this.afterId &&
        other.beforeId === this.beforeId &&
        isSameData(other.after, this.after) &&
        isSameData(other.before, this.before) &&
        other.seen === this.seen;
  }

  markAsSeen(): this {
    return new CalendarEntryRescheduledEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.after.clone(),
        this.before.clone(),
        this.afterId,
        this.beforeId,
    ) as this;
  }
}

export class CalendarEntryRescheduledEventBuilder extends DataModelBuilder<CalendarEntryRescheduledEvent> {
  build(context: SerializationContext, map: SerializedData): CalendarEntryRescheduledEvent {
    return CalendarEntryRescheduledEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CalendarEntryRescheduledEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CalendarEntryRescheduledEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
